/* ============================================================
   pn-note3d.js — the price sticky note, in real 3D.

   Rue's model (StickyNotesPushPin3W.obj): a curled sticky note with a
   pushpin through its top edge. Three objects — Plane002 is the leaf,
   Cylinder001 the pin head, Object001 the pin's shaft.

   Notes on how this is put together:

   · Three is pulled from the SAME module URL the turntable uses. An ES
     module is instantiated once per URL, so this is the existing Three,
     not a second copy — no extra download and no duplicated WebGL globals.

   · The geometry ships as indexed JSON rather than an .obj, so no loader
     has to come with it. It was recentred on the note's footprint and
     normalised to one unit across at build time; every number below is in
     those units.

   · The leaf is scaled on X to the element's aspect. The note is nearly
     square and the buy panel is a wide strip, so without this it would sit
     in the middle with wood either side. The PIN IS NOT SCALED — it is a
     separate object and stays round.

   · Nothing renders unless something moved. One frame on load, then frames
     only while a hover transition is settling. There is already a WebGL
     canvas on this page (the deck) and neither should be spinning a rAF for
     a still image.

   · The CSS note underneath is the fallback. It is only hidden once a frame
     has actually been drawn, so no WebGL, a failed import or a slow asset
     leaves a working note on screen rather than a hole.
   ============================================================ */
(function () {
  'use strict';

  var host = document.querySelector('.pnsn-note--buy');
  var zone = document.querySelector('.pnsn-buyzone') || host;   /* hover target */
  if (!host) return;

  var A = window.PN_N3D_ASSETS || {};

  /* Resolve siblings against this file's own URL — and, just as importantly,
     MENTION import.meta AT ALL.

     Shopify runs theme .js through esbuild. A file with no static import,
     no export and no `import.meta` is classified as a classic script, and
     esbuild then rewrites `import(x)` into `Promise.resolve().then(() =>
     __toESM(require(x)))` — which throws `require is not defined` in the
     browser and took the whole module down silently. pn-turntable.js gets
     away with the same dynamic import purely because it happens to use
     import.meta.url, which forces ESM classification. This does the same,
     deliberately. Do not "tidy" it away. */
  var rel = function (f) { return new URL('./' + f, import.meta.url).href; };

  var reduced = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) { return false; }
  }
  if (!hasWebGL()) return;                 /* CSS note stays */

  var canvas = document.createElement('canvas');
  canvas.className = 'pnsn-note3d';
  canvas.setAttribute('aria-hidden', 'true');
  host.insertBefore(canvas, host.firstChild);

  var THREE, renderer, scene, camera, group, leaf, leafPivot, pinHead, pinShaft, shadowPlane;
  var raf = null, hover = false, lift = 0, target = 0, drawnOnce = false;
  var vel = 0, roll = 0, last = 0;
  /* the price is HTML on top of the canvas; these carry it with the paper */
  var ink = null, hingeCss = '50% 0%';

  /* 0.72 rad is ~41 degrees: the leaf foreshortens to cos(41) of its depth,
     so a bit over a quarter of the wall below it comes clear — enough to
     read the lettering underneath. */
  var LIFT = 0.72;           /* radians the leaf swings up on its pin */
  var TILT = -0.13;          /* the whole pinned assembly leans back a little */

  function render() {
    renderer.render(scene, camera);
    if (!drawnOnce) {
      drawnOnce = true;
      host.classList.add('pnsn-3d-on');    /* only now retire the CSS note */
    }
  }

  /* A spring, not an exponential ease.

     `lift += (target - lift) * 0.18` decelerates into the target and stops
     dead, which is why it read as a mechanism rather than a sheet of paper.
     A damped spring carries momentum: the leaf swings a little past where it
     is going, comes back, and settles. Stiffness and damping are tuned just
     under critical, so it overshoots once and does not wobble.

     dt is taken from the frame clock rather than assumed, so the motion is
     the same on a 120Hz display as on a 60Hz one, and it is clamped so a
     backgrounded tab cannot resume with one enormous step. */
  var K = 150;               /* stiffness */
  var DAMP = 15.5;           /* just under critical: one small overshoot */

  function tick(ts) {
    if (!last) last = ts;
    var dt = Math.min((ts - last) / 1000, 1 / 30);
    last = ts;

    var acc = (target - lift) * K - vel * DAMP;
    vel += acc * dt;
    lift += vel * dt;

    /* The roll trails the lift instead of tracking it, so one corner leads
       and the far one catches up — that lag is most of what makes it look
       like paper peeling rather than a panel hinging. */
    roll += (lift - roll) * Math.min(1, dt * 7.5);

    if (Math.abs(target - lift) < 0.0015 && Math.abs(vel) < 0.006) {
      lift = target; vel = 0; roll = target;
      place(); render(); raf = null; last = 0; return;
    }
    place(); render();
    raf = requestAnimationFrame(tick);
  }
  function kick() { if (!raf) { last = 0; raf = requestAnimationFrame(tick); } }

  /* The pin holds the note's top edge, so that edge is the hinge: the foot
     swings up and towards the viewer and the paper foreshortens away from
     the bottom of the panel, uncovering the button stuck to the wall behind.
     Nothing slides — it pivots, the way a pinned note actually would. */
  function place() {
    if (!leafPivot) return;
    leafPivot.rotation.x = -lift;
    /* Uneven on purpose. A real note does not come away from the wall square
       — it lifts on one side first, because it is held at a single point and
       the paper is not stiff. The roll runs off the LAGGED value, so the
       twist is still catching up while the foot is already rising. */
    leafPivot.rotation.z = roll * 0.13;
    leafPivot.rotation.y = roll * 0.05;
    /* No translation toward the camera. Raising the whole leaf lifted the
       pinned edge too, and in a top-down view that put paper in front of the
       pin — the note climbed over the tack holding it. The pin holds that
       edge; only the rotation is allowed to move the sheet. */

    /* The camera is ORTHOGRAPHIC, and a CSS rotateX with no perspective
       foreshortens linearly — which is precisely what an orthographic
       projection does. So the same angles applied here land the text on the
       paper exactly, with no perspective term to match up. Add one and they
       would diverge. */
    if (ink) {
      var deg = lift * 57.2958;
      ink.style.transform =
        'rotateX(' + deg.toFixed(2) + 'deg) ' +
        'rotateZ(' + (-roll * 0.13 * 57.2958).toFixed(2) + 'deg)';
    }
  }

  function fit() {
    var w = host.clientWidth, h = host.clientHeight;
    if (!w || !h || !renderer) return;
    /* The canvas is deliberately larger than the note so the cast shadow has
       somewhere to land. Render at the canvas's size, and widen the frustum
       by the same factor so the leaf still comes out the size of the note. */
    var cw = canvas.clientWidth || w, ch = canvas.clientHeight || h;
    renderer.setSize(cw, ch, false);
    var bleed = cw / w;

    /* NOTHING is scaled. The leaf was previously stretched on X to fill a
       wide panel while the pin was left at its true size, and that mismatch
       is what made the pin look deformed. The panel now carries the model's
       aspect instead, and the frustum below is widened to the canvas rather
       than the model being squeezed into it.

       Frame on the leaf and the pin ONLY: the shadow catcher is 6 units
       across, six times the note, so measuring the whole group set a frustum
       six times too wide and the note rendered as a speck. */
    var all = new THREE.Box3().setFromObject(leafPivot);
    all.union(new THREE.Box3().setFromObject(pinHead));
    var s = all.getSize(new THREE.Vector3());
    var c = all.getCenter(new THREE.Vector3());
    var halfW = s.x / 2 * 1.05 * bleed, halfH = s.z / 2 * 1.08 * bleed;
    /* widen whichever axis is short so the render can never distort, however
       the panel is sized */
    var aspect = cw / ch;
    if (halfW / halfH < aspect) halfW = halfH * aspect; else halfH = halfW / aspect;
    camera.position.set(c.x, 4, c.z);
    camera.lookAt(c.x, 0, c.z);
    camera.left = -halfW; camera.right = halfW;
    camera.top = halfH;  camera.bottom = -halfH;
    camera.updateProjectionMatrix();

    /* Where the leaf's hinge lands on screen, in element pixels. The HTML
       price pivots about exactly the same line the mesh does, so the two stay
       registered instead of drifting apart as the leaf swings. Projected
       rather than guessed, so it follows any change to the framing. */
    if (ink) {
      var hinge = new THREE.Vector3(0, 0, leafPivot.position.z);
      group.localToWorld(hinge);
      hinge.project(camera);
      /* in the INK wrapper's coordinates (the note's box), not the canvas's */
      var offX = (cw - w) / 2, offY = (ch - h) / 2;
      hingeCss = ((hinge.x * 0.5 + 0.5) * cw - offX).toFixed(1) + 'px ' +
                 ((-hinge.y * 0.5 + 0.5) * ch - offY).toFixed(1) + 'px';
      ink.style.transformOrigin = hingeCss;
    }
  }

  function geom(part, uv) {
    var g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(part.position, 3));
    g.setIndex(part.index);
    g.computeVertexNormals();
    /* The model carries no UVs at all — it was exported with only positions,
       so a texture map had nothing to sample and the leaf rendered as flat
       colour. Project the paper on from directly above: u across the note,
       v down it. Good enough for a grain, and it stays put when the leaf is
       scaled, because the projection is computed before the scale. */
    if (uv) {
      var pos = part.position, n = pos.length / 3;
      var minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, i;
      for (i = 0; i < n; i++) {
        var x = pos[i * 3], z = pos[i * 3 + 2];
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
      }
      var dx = (maxX - minX) || 1, dz = (maxZ - minZ) || 1, out = new Float32Array(n * 2);
      for (i = 0; i < n; i++) {
        out[i * 2]     = (pos[i * 3] - minX) / dx;
        out[i * 2 + 1] = (pos[i * 3 + 2] - minZ) / dz;
      }
      g.setAttribute('uv', new THREE.BufferAttribute(out, 2));
    }
    return g;
  }

  var booting = false;
  function boot() {
    if (booting || renderer) return;
    booting = true;

    Promise.all([
      import(A.three || rel('pn-three.js')),
      fetch(A.geo || rel('pn-note3d.json')).then(function (r) { return r.json(); })
    ]).then(function (res) {
      THREE = res[0];
      var G = res[1];

      renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;

      scene = new THREE.Scene();
      camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -10, 10);
      camera.position.set(0, 4, 0);
      camera.up.set(0, 0, -1);              /* -Z is up the screen, so the pin is at the top */
      camera.lookAt(0, 0, 0);

      group = new THREE.Group();
      group.rotation.x = TILT;
      scene.add(group);

      /* --- the leaf --- */
      var paper = null;
      if (A.paper) {
        paper = new THREE.TextureLoader().load(A.paper, function () { kick(); });
        paper.colorSpace = THREE.SRGBColorSpace;
        paper.wrapS = paper.wrapT = THREE.RepeatWrapping;
        /* The source photograph is a folded sheet: it has a hard crease
           running across its middle and another down it. Sampling from 0
           dragged that crease across the note, and once the leaf lifted it
           read as a dashed line ruled over the paper. This window sits below
           the horizontal fold and inside the vertical one, so only the grain
           comes through. */
        paper.offset.set(0.06, 0.545);
        paper.repeat.set(0.88, 0.40);
      }
      leaf = new THREE.Mesh(geom(G.Plane002, true), new THREE.MeshStandardMaterial({
        color: 0xffe9a8,                    /* Rue's white stock, warmed to a sticky note */
        map: paper,
        roughness: 0.94,
        metalness: 0.0,
        side: THREE.DoubleSide
      }));
      leaf.castShadow = true;
      /* hinge the leaf on its pinned edge rather than its centre */
      var lb = new THREE.Box3().setFromObject(leaf);
      leafPivot = new THREE.Group();
      leafPivot.position.z = lb.min.z;
      leaf.position.z = -lb.min.z;
      leafPivot.add(leaf);
      group.add(leafPivot);

      /* --- the pin --- */
      pinHead = new THREE.Mesh(geom(G.Cylinder001), new THREE.MeshStandardMaterial({
        color: 0xf0460a, roughness: 0.32, metalness: 0.06
      }));
      pinShaft = new THREE.Mesh(geom(G.Object001), new THREE.MeshStandardMaterial({
        color: 0xc9d0d6, roughness: 0.24, metalness: 0.85
      }));
      pinHead.castShadow = true;
      group.add(pinHead, pinShaft);

      /* --- the wall the note is pinned to: catches shadow, paints nothing --- */
      /* 2.6, not 6. The catcher used to run far outside the shadow camera's
         +/-1.4 frustum, so mid-animation the shadow ran off the edge of the
         map and the boundary showed as a pale halo at the top left. The plane
         and the shadow camera now cover the same ground. */
      shadowPlane = new THREE.Mesh(
        new THREE.PlaneGeometry(3.4, 3.4),
        new THREE.ShadowMaterial({ opacity: 0.26 })
      );
      shadowPlane.rotation.x = -Math.PI / 2;
      /* Well clear of the leaf. At -0.004 the catcher was all but coplanar
         with the paper wherever the sheet lies flat, and the two surfaces
         stippled against each other as it settled. */
      shadowPlane.position.y = -0.03;
      shadowPlane.receiveShadow = true;
      group.add(shadowPlane);

      /* --- light: warm key from the upper left, matching the page --- */
      scene.add(new THREE.HemisphereLight(0xfff6ea, 0x8a7050, 1.35));
      var key = new THREE.DirectionalLight(0xfff6ec, 2.0);
      key.position.set(-1.5, 2.4, -1.1);
      key.castShadow = true;
      key.shadow.mapSize.set(1024, 1024);
      /* Wide enough for the whole cast shadow, not just the leaf. Once the
         note swings up to 41 degrees the light throws its shadow well past
         the sheet's own footprint; a +/-1.3 frustum cut that throw off and
         the clipped end read as a hard grey rectangle on the wood. */
      key.shadow.camera.left = -2.3; key.shadow.camera.right = 2.3;
      key.shadow.camera.top = 2.3;  key.shadow.camera.bottom = -2.3;
      key.shadow.radius = 3.5;
      /* the leaf is a single-sided sheet lying almost flat on its receiver,
         which is the worst case for shadow acne as it rotates */
      key.shadow.bias = -0.0008;
      key.shadow.normalBias = 0.02;
      scene.add(key);
      var fill = new THREE.DirectionalLight(0xdfe9ff, 0.35);
      fill.position.set(1.2, 1.0, 0.9);
      scene.add(fill);

      /* Move the price into a wrapper that can be transformed as one. The
         text has to travel with the leaf — pinned to a sheet that swings
         while the writing on it stays flat is the single most artificial
         thing about a hover like this. */
      var priceRow = host.querySelector('.pnsn-pricerow');
      if (priceRow) {
        ink = document.createElement('div');
        ink.className = 'pnsn-note3d-ink';
        priceRow.parentNode.insertBefore(ink, priceRow);
        ink.appendChild(priceRow);
      }

      fit();
      place();
      render();

      if (window.ResizeObserver) new ResizeObserver(function () {
        fit(); render();
      }).observe(host);
    }).catch(function (e) {
      /* Leave the CSS note in place; it was never hidden. Recorded rather
         than swallowed — a silent catch here cost real time once already. */
      window.__pnNote3dError = String((e && e.stack) || e);
      booting = false;
    });
  }

  function setHover(on) {
    hover = on;
    target = (on && !reduced) ? LIFT : 0;
    if (renderer) kick(); else boot();
  }
  /* on the zone, not the note: the note is pointer-transparent so the button
     underneath stays clickable through it */
  zone.addEventListener('pointerenter', function () { setHover(true); });
  zone.addEventListener('pointerleave', function () { setHover(false); });
  zone.addEventListener('focusin',  function () { setHover(true); });
  zone.addEventListener('focusout', function () { setHover(false); });

  /* Build it when the panel is actually on screen — After Hours visitors
     never pay for it. */
  function active() {
    return (document.documentElement.getAttribute('data-pn-mode') || 'sticky') === 'sticky';
  }
  if (active()) boot();
  window.addEventListener('pn:mode', function () { if (active()) { boot(); if (renderer) { fit(); render(); } } });
})();
